import React from 'react';
import styled from 'styled-components';
import {
    EVENT_INITIATORS,
    INTERACTION_TYPES,
    withCustomMetrics,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { useEffectOnce } from '@zocdoc-frontend-common/utils-react';
import { CompactModal } from '@zocdoc-mezzanine/components-modal';
import { paragraphSmall } from '@zocdoc-mezzanine/foundations-typography';
import { MODAL_STRINGS } from './constants';

/**
 *
 * @param {string} ariaLabel
 * @param {string} bookingId
 * @param {string} modalName
 * @param {string} headerText
 * @param {string|JSX.Element|undefined} descriptionText
 * @param {function} onClickPrimaryButton
 * @param {string|null} primaryButtonText
 * @param {function|null} onClickSecondaryButton - optional
 * @param {string|null} secondaryButtonText - optional
 * @param {function} onClickClose
 * @param {function} sendMetric
 * @returns {JSX.Element}
 * @constructor
 */

const BaseModalContentView = ({
    ariaLabel,
    bookingId,
    modalName,
    headerText,
    descriptionText,
    onClickPrimaryButton,
    primaryButtonText = null,
    onClickSecondaryButton = null,
    secondaryButtonText = null,
    onClickClose,
    sendMetric,
}) => {
    useEffectOnce(() => {
        if (
            headerText === MODAL_STRINGS.MaximumVaccineAppointmentHeaderDose1 ||
            headerText === MODAL_STRINGS.MaximumVaccineAppointmentHeaderDose2 ||
            headerText === MODAL_STRINGS.MaximumVaccineAppointmentHeaderBooster
        ) {
            sendMetric({
                action: 'View vaccine fraud modal',
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: 'Vaccine Fraud Modal',
                element: 'Error',
            });
        } else {
            sendMetric({
                action: `View ${modalName}`,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.VIEW,
                component: modalName,
                element: modalName,
                eventDetails: {
                    bookingId: bookingId,
                },
            });
        }
    });

    const handleClickPrimaryCTA = () => {
        sendMetric({
            action: `Click ${primaryButtonText} CTA`,
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
            component: modalName,
            element: 'Primary CTA',
            eventDetails: {
                bookingId: bookingId,
            },
        });
        onClickPrimaryButton();
    };

    const primaryButtonProps = {
        children: primaryButtonText,
        onClick: handleClickPrimaryCTA,
    };

    const handleClickClose = () => {
        sendMetric({
            action: 'Click Close icon',
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.CLICK,
            component: modalName,
            element: 'Close icon',
            eventDetails: {
                bookingId: bookingId,
            },
        });
        onClickClose();
    };

    if (!!secondaryButtonText) {
        const handleClickSecondaryCTA = () => {
            sendMetric({
                action: `Click ${secondaryButtonText} CTA`,
                initiator: EVENT_INITIATORS.USER,
                interactionType: INTERACTION_TYPES.CLICK,
                component: modalName,
                element: 'Secondary CTA',
                eventDetails: {
                    bookingId: bookingId,
                },
            });
            onClickSecondaryButton();
        };

        const secondaryButtonProps = {
            children: secondaryButtonText,
            onClick: handleClickSecondaryCTA,
        };

        return (
            <CompactModal
                aria-label={ariaLabel}
                title={headerText}
                primaryButtonProps={primaryButtonProps}
                secondaryButtonProps={secondaryButtonProps}
                onClose={handleClickClose}
            >
                {descriptionText && (
                    <Description data-test="modal-description">
                        {descriptionText}
                    </Description>
                )}
            </CompactModal>
        );
    }

    return (
        <CompactModal
            aria-label={ariaLabel}
            title={headerText}
            primaryButtonProps={primaryButtonProps}
            onClose={handleClickClose}
        >
            {descriptionText && (
                <Description data-test="modal-description">
                    {descriptionText}
                </Description>
            )}
        </CompactModal>
    );
};

export const ModalContentView = withCustomMetrics({
    page: 'Booking',
    category: 'Booking - Appt Info',
    section: 'Booking Error Modal',
})(BaseModalContentView);

const Description = styled.p`
    ${paragraphSmall}
`;
